import { useTranslation } from 'react-i18next'
import { useSlotObject, useSlotT } from '@/cms/useSlotT'
import { Icon } from '@iconify/react'
import { Container, Card, Reveal, PageHeader } from '@/components/ui'
import { H2 } from '@/components/ui/typography'
import SEO from '@/utils/seo'
import { buildAboutPageJsonLd } from '@/utils/structuredData'
import { localizedServiceAreas } from '@/content/cms/serviceAreaLocale'
import type { Locale } from '@/utils/localePath'
import SupportProcess from '@/components/about/SupportProcess'

interface TextItem {
  title: string
  text: string
}

const MISSION_ICONS = [
  'lucide:graduation-cap',
  'lucide:workflow',
  'lucide:book-open-check',
  'lucide:users',
]

export default function AboutPage() {
  const { t, i18n } = useTranslation()
  const slotT = useSlotT('about')
  const slotObj = useSlotObject('about')
  // The four goal cards are a JSON slot: the i18n default defines the shape
  // (four cards, title + text) and the CMS override is merged onto it
  // index-wise, so the number of rendered cards cannot change.
  const missionItems = slotObj('about.mission.items') as TextItem[]
  // The four service-area cards come from the CMS, which stores their copy
  // once per locale; resolve the active locale's text (the generated index
  // falls back to zh-Hant if a locale is missing).
  const locale = (i18n.language || 'zh-Hant') as Locale
  const serviceAreas = localizedServiceAreas(locale)

  return (
    <>
      <SEO
        title={slotT('seo.aboutTitle')}
        description={slotT('seo.aboutDescription')}
        keywords={slotT('seo.aboutKeywords')}
        jsonLd={buildAboutPageJsonLd({
          description: slotT('seo.organizationDescription'),
          founderName: slotT('seo.founderName'),
          // `seo.founderJobTitle` is shared with the home page's Person
          // schema, so it stays on plain i18n: one page must not own a key
          // another page renders, or the two would drift apart.
          founderJobTitle: t('seo.founderJobTitle'),
        })}
      />
      {/* The hero eyebrow tag (`about.hero.tag`) is intentionally not passed:
          the page opens straight into the title, matching the cleaner
          /lectures hero. */}
      <PageHeader
        title={slotT('about.hero.title')}
        description={slotT('about.hero.description')}
        className="bg-gradient-to-br from-surface via-surface-2 to-surface-3 relative overflow-hidden"
      />

      {/* Tension beat — warm sunrise band so it reads as hope, not alarm */}
      <section
        className="relative py-section-y px-section-x bg-gradient-to-br from-yada-gold-soft via-yada-gold to-yada-green text-ink overflow-hidden"
        aria-labelledby="origin-title"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-16 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-yada-green/10 blur-3xl"
        />
        <Container>
          <Reveal>
            <div className="max-w-3xl relative">
              <span className="section-title__tagline inline-block mb-3 text-primary-dark">
                {slotT('about.origin.tag')}
              </span>
              <h2
                id="origin-title"
                className="text-3xl md:text-4xl font-bold text-ink mb-6"
              >
                {slotT('about.origin.title')}
              </h2>
              <p className="text-lg md:text-xl text-ink-soft leading-relaxed border-l-2 border-primary-dark/60 pl-5">
                {slotT('about.origin.text')}
              </p>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Answer beat — back to cream, cards carry the weight */}
      <section className="py-section-y px-section-x bg-surface" aria-labelledby="mission-title">
        <Container>
          <Reveal>
            <div className="max-w-3xl mb-10">
              <span className="section-title__tagline inline-block mb-3 hidden!">
                {slotT('about.mission.tag')}
              </span>
              <h2 id="mission-title" className="text-3xl md:text-4xl font-bold text-ink">
                {slotT('about.mission.title')}
              </h2>
              <p className="mt-4 text-lg text-ink-soft leading-relaxed">
                {slotT('about.mission.text')}
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-gap-cards">
            {missionItems.map((item, idx) => (
              <Reveal key={idx} delay={idx * 0.1}>
                <Card hover bordered className="h-full">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-yada-lg bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center flex-shrink-0 shadow-yada">
                      <Icon
                        icon={MISSION_ICONS[idx % MISSION_ICONS.length]}
                        className="text-surface"
                        style={{ fontSize: '1.5rem' }}
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-ink mb-2">{item.title}</h3>
                      <p className="text-ink-soft leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/*
        我們也是教育者 — moved here from /products on 2026-11-04.

        The page used to end with the organisation's closing line right after
        「我們是誰？」. It now reads as ONE story, in the order the words
        themselves ask for:

          1. 頁首              — 願景：我們為何聚集
          2. 時代挑戰          — 為何此刻需要生涯規劃
          3. 我們是誰？        — 誰在做事
          4. 我們也是教育者    — 另一個身分：我們也為學校造工具
          5. 四大服務範疇      — 我們做什麼
          6. 我們如何與學校同行 — 我們怎樣與學校一起做事
          7. 結語              — 一句話收尾

        4 sits directly after 3 because both answer the same question — 我們
        是誰 — from two sides: the people behind the work (the mission
        cards) and the role we take on for schools (educators). 5 and 6 then
        answer, in that order, 做什麼 and 怎樣做.

        LAYOUT — the block keeps the two-column split it had on /products:
        the claim (heading + subtitle) on the left, the supporting
        paragraphs on the right, with the gold rule the section taglines
        use elsewhere on the site.
      */}
      <section
        className="py-section-y px-section-x bg-gradient-to-br from-surface-2 to-surface-3"
        aria-labelledby="educators-title"
      >
        <Container>
          <Reveal>
            <div className="grid gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-14">
              <div>
                {/* The gold rule that `.section-title__tagline::before`
                    draws on every other section heading, kept as its own
                    element so this block can carry it without inheriting
                    the tagline's left padding and text styles. */}
                <span aria-hidden="true" className="block w-12 h-1 rounded-full bg-accent mb-5" />
                <H2 id="educators-title" className="text-3xl md:text-4xl font-bold text-ink">
                  {slotT('about.educators.title')}
                </H2>
                <p className="mt-3 text-lg md:text-xl font-semibold leading-relaxed text-primary">
                  {slotT('about.educators.subtitle')}
                </p>
              </div>

              <div className="space-y-5 text-ink-soft leading-relaxed">
                <p>{slotT('about.educators.p1')}</p>
                <p>{slotT('about.educators.p2')}</p>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/*
        四大服務範疇 — moved here from /products on 2026-11-04, and now
        introduced by the services lede (`about.servicesIntro`), which used
        to sit at the top of the products page.

        The lede belongs to this section: it says what our services span and
        that they build a 「學校—機構—職場」 ecosystem — which is exactly
        what the four cards below then break down. On the products page it
        read as a promise about products; here it reads as a summary of the
        organisation's work.

        The four cards are the CMS rows (cms_service_areas), so the copy an
        operator edits in the admin is the copy that shows here — icon,
        gradient and order included.
      */}
      <section className="py-section-y px-section-x bg-surface" aria-labelledby="areas-title">
        <Container>
          <Reveal>
            <p className="text-lg md:text-xl text-ink-soft leading-relaxed max-w-3xl mb-8">
              {slotT('about.servicesIntro')}
            </p>
            <h2 id="areas-title" className="border-l-4 border-accent pl-4 text-3xl md:text-4xl font-bold text-ink">
              {slotT('about.areas.title')}
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-gap-cards mt-8">
            {serviceAreas.map((area, idx) => (
              <Reveal key={area.slug} delay={idx * 0.1}>
                <Card hover bordered className="h-full">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-yada-lg bg-gradient-to-br ${area.accent_gradient} flex items-center justify-center flex-shrink-0 shadow-yada`}>
                      <Icon
                        icon={area.icon}
                        className="text-surface"
                        style={{ fontSize: '1.75rem' }}
                      />
                    </div>
                    <div>
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-sm font-bold text-accent">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <h3 className="text-xl font-bold text-ink">{area.title}</h3>
                      </div>
                      <p className="text-ink-soft leading-relaxed">{area.description}</p>
                    </div>
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/*
        我們如何與學校同行 — the three-step band, moved here from /products
        on 2026-11-04 (the component itself moved from
        `components/home/PricingProcess.tsx` to `components/about/`).

        It comes last of the three because it is the answer to the
        question the previous sections raise: what is actually 導入 like
        when a school works with us. 導入前 / 導入中 / 導入後 is the
        concrete shape of 「怎樣與學校同行」, so it belongs after the
        description of what we do, and before the closing line.
      */}
      <SupportProcess />

      {/* Closing */}
      <section className="py-section-y px-section-x bg-gradient-to-br from-primary via-primary-dark to-yada-olive text-surface">
        <Container>
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <Icon
                icon="lucide:rocket"
                className="text-accent mx-auto mb-6"
                style={{ fontSize: '4rem' }}
              />
              <p className="text-2xl md:text-3xl lg:text-4xl font-bold leading-relaxed">
                {slotT('about.closing.quote')}
              </p>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  )
}
